import React, { ReactElement, ReactNode } from 'react'
import classNames from 'classnames'
import Price from '@/components/common/price'
import KMWLabel from '@/components/elements/KMWLabel'
import Stars from '@/components/common/stars'
import Button from '@/components/common/button/Button'
import { LikeHotelButton } from '@/components/elements/hotel_tease_element/LikeHotelButton'
import { whitelabel } from '@/lib/serverHelpers'
import { NumberOfPreloadedImages } from '@/components/ui/search/image-slider/constants'
import { ButtonIcon } from '@/components/common/button/constants'
import { getHotelName, renderXTimes } from '@/lib/utils/utils'
import ImageSlider, {
  LANDING_PAGE_SIZES,
  POPUP_SIZES,
} from '@/components/ui/search/image-slider/ImageSlider'
import { TopDisruptor } from '@/components/elements/hotel_tease_element/TopDisruptor'

import { TopRankingWrapper } from '@/components/ui/search/TopRankingWrapper'

function TitleStar(): ReactElement {
  return (
    <div className="z-40 inline-block h-2.5 w-2.5 items-center before:absolute before:h-2.5 before:w-2.5 before:translate-x-[0px] before:translate-y-[-6px] before:bg-contain before:bg-center before:bg-no-repeat before:content-[''] before:icon-title-star"></div>
  )
}

export interface HotelTeaserBox {
  topBar?: string | undefined
  reference?: (div: HTMLDivElement) => void
  withinPopup?: boolean
  specialCategory?: string
  priceLine2?: string
  priceLine1?: string
  discount?: string
  hotelName: string
  images: string[]
  url: string
  hotelCode: string
  destinations?: string[]
  annotation: string
  isKmwHotel: boolean
  hotelCategoryStars?: number
  hotelCategoryText?: string
  ratingValue?: number
  ratingText: string
  programName: string
  buttonLabel: ReactNode
  hasTopbarsWithinRow?: boolean
}

export function HotelTeaserBox(props: HotelTeaserBox): ReactElement {
  return (
    <TopRankingWrapper topBar={props.topBar}>
      <div
        className={classNames(
          'flex-grow flex-col overflow-hidden rounded-md bg-white shadow-md sm:flex-row lg:flex lg:flex-col',
          {
            'w-[260px]': props.withinPopup,
            'lg:mx-[3px] lg:mb-[3px] lg:mt-8': props.hasTopbarsWithinRow,
          },
        )}
        ref={props.reference}
      >
        <div
          className={classNames('group relative flex flex-col', {
            'h-[200px]': !props.withinPopup,
            'h-[152px]': props.withinPopup,
          })}
        >
          {props.priceLine2 && (
            <Price
              priceLine1={props.priceLine1}
              priceLine2={props.priceLine2}
              small={props.withinPopup}
            />
          )}
          {props.discount && (
            <div
              className={classNames(
                'absolute right-0 z-10 rounded-bl-md bg-highlight-red py-1 pl-2 pr-2 text-right text-white ',
                {
                  'top-[58px]': !props.withinPopup,
                  'top-[44px]': props.withinPopup,
                },
              )}
            >
              <div className="text-sm font-regular leading-none">{props.discount}</div>
            </div>
          )}
          {props.isKmwHotel && <KMWLabel />}

          <LikeHotelButton
            hotelCode={props.hotelCode}
            additionalClassName="top-1 left-2 absolute"
            colorUnselected="rgba(0,0,0,0.33)"
            strokeUnselected="white"
            strokeSelected="white"
            greyBackground={true}
          />
          {props.annotation && props.specialCategory && (
            <TopDisruptor
              displayedKey={props.specialCategory || ''}
              value={props.annotation || ''}
              additionalClassName={classNames('absolute bottom-2 right-2 flex shadow-md ml-2 z-10')}
            />
          )}
          <div
            className={classNames('w-full overflow-hidden', {
              'md:h-50 sm:h-70 h-60 lg:h-60 xl:h-56': !props.withinPopup,
              'h-52': props.withinPopup,
            })}
          >
            <ImageSlider
              numberOfImagesToPreload={
                props.withinPopup
                  ? NumberOfPreloadedImages.ForLikelyUse
                  : NumberOfPreloadedImages.ForMediumLikelyUse
              }
              images={props.images}
              pictureSizeDefinition={props.withinPopup ? POPUP_SIZES : LANDING_PAGE_SIZES}
              openInNewWindow={props.withinPopup || props.isKmwHotel}
              href={props.url}
              alt={props.hotelName}
            />
          </div>
        </div>

        <div
          className={classNames(
            'relative flex w-full min-w-0 flex-col lg:flex-grow lg:justify-between',
            {
              'p-3': !props.withinPopup,
              'px-2 pb-2 pt-1': props.withinPopup,
            },
          )}
        >
          <div className="flex w-full flex-grow flex-col items-start">
            <a
              href={props.url}
              {...(props.isKmwHotel || props.withinPopup
                ? { target: whitelabel ? undefined : '_blank', rel: 'noopener noreferrer' }
                : {})}
              className={classNames(
                "font-medium text-stone-600 after:pointer-events-auto after:absolute after:inset-0 after:z-20 after:bg-[rgba(0,0,0,0)] after:content-['']",
                {
                  'mr-2 text-xl': !props.withinPopup,
                  'text-lg': props.withinPopup,
                },
              )}
            >
              {getHotelName(props.hotelName)[0]}{' '}
              <span className="whitespace-nowrap">
                {getHotelName(props.hotelName)[1]}{' '}
                {(props.hotelCategoryStars && props.hotelCategoryStars > 0 && (
                  <div className="inline-block">
                    {renderXTimes(props.hotelCategoryStars, TitleStar)}
                  </div>
                )) ||
                  ''}
                {props.hotelCategoryText && (
                  <div className="ml-0.5 inline-block -translate-y-[3px] align-text-bottom text-[10px] uppercase text-stone-600">
                    {props.hotelCategoryText}
                  </div>
                )}
              </span>
            </a>

            {!props.withinPopup && (
              <div className="mb-2 text-xs font-regular leading-tight text-stone-600">
                {props.destinations &&
                  props.destinations.map((dest, index) => (
                    <span key={dest + index.toString()}>
                      {dest}
                      {index < (props.destinations as string[]).length - 1 && ' · '}
                    </span>
                  ))}
              </div>
            )}
          </div>
          <div className="flex flex-col">
            {props.programName && (
              <div className="mt-1 h-8 overflow-hidden text-ellipsis whitespace-nowrap text-2xl font-medium leading-tight text-stone-700">
                {props.programName}
              </div>
            )}
            <div className="grid grid-cols-[1fr_auto]">
              {props.ratingText && !props.withinPopup && (
                <div className="text-sm">
                  <div className="font-medium leading-tight text-stone-700">{props.ratingText}</div>
                  {props && <Stars rating={props.ratingValue} />}
                </div>
              )}
              <Button
                className="col-start-2 mt-auto text-white"
                icon={ButtonIcon.ARROW_RIGHT}
                smaller={true}
              >
                {props.buttonLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </TopRankingWrapper>
  )
}
